import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Search } from "lucide-react";
import OperationsShell from "@/components/operations/OperationsShell";
import MasterDetailLayout from "@/components/layout/MasterDetailLayout";

/**
 * Standard Operations master/detail page scaffold.
 *
 * Every Operations list-and-detail module (Projects, Engineers,
 * Calendar, Tasks, Customers, Bookings/Appointments, Documents,
 * Notifications) should render through this component so the shell,
 * spacing, toolbar rhythm and empty states stay identical across the
 * Operations workspace.
 *
 * Callers own the data — this scaffold owns the layout, the standard
 * search input, filter chips, list summary and the "select a row"
 * empty state. It never fetches or transforms.
 *
 * Props:
 *   eyebrow            — small uppercase label above the title
 *   title              — page title
 *   icon               — lucide icon component
 *   primaryAction      — optional element (button / link) on the right of the header
 *   backHref           — override the back link (defaults to /operations)
 *   backLabel          — override the back label
 *
 *   searchQuery        — controlled search value ("" to hide input)
 *   onSearchChange     — setter
 *   searchPlaceholder  — placeholder for the search input
 *
 *   filterChips        — array of { key, label, count?, active }
 *   onFilterChipClick  — (key) => void
 *
 *   loading            — boolean; renders a full-list loader
 *   items              — array of records
 *   getItemId          — (item) => stable id
 *   renderItem         — (item, { active, onSelect }) => list row
 *   listSummary        — small text/element rendered above the list (record counts, sort hint)
 *   emptyListState     — element rendered when items is empty
 *
 *   selectedId         — currently selected item id
 *   onSelect           — (item | null) => void
 *   selectedItem       — the resolved selected record (from parent)
 *   renderDetail       — (item) => detail body
 *   renderDetailHeader — (item) => optional sticky header inside detail pane
 *   emptyDetailState   — element rendered when nothing is selected on desktop
 *   testidPrefix       — data-testid prefix for the shell
 */
export default function OperationsMasterDetailPage({
    eyebrow = "Operations",
    title,
    icon,
    primaryAction = null,
    backHref = "/operations",
    backLabel = "Back to Operations",
    searchQuery = "",
    onSearchChange = null,
    searchPlaceholder = "Search",
    filterChips = null,
    onFilterChipClick = null,
    headerBanner = null,
    loading = false,
    items = [],
    getItemId = (i) => i?.id,
    renderItem,
    listSummary = null,
    emptyListState = null,
    selectedId = null,
    onSelect,
    selectedItem = null,
    renderDetail,
    renderDetailHeader = null,
    emptyDetailState = null,
    testidPrefix = "ops-master-detail",
}) {
    const toolbar = useMemo(() => {
        if (!onSearchChange && !filterChips) return null;
        return (
            <div className="flex flex-col md:flex-row md:items-center gap-3">
                {onSearchChange && (
                    <div className="relative flex-1 min-w-0 max-w-xl">
                        <Search size={14} strokeWidth={1.75}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5565]" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => onSearchChange(e.target.value)}
                            placeholder={searchPlaceholder}
                            data-testid={`${testidPrefix}-search`}
                            className="w-full h-9 pl-9 pr-3 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F] placeholder-[#9AA0AA] focus:outline-none focus:ring-2 focus:ring-[#131B2F]/10 focus:border-[#131B2F]"
                        />
                    </div>
                )}
                {filterChips && filterChips.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap" data-testid={`${testidPrefix}-filter-chips`}>
                        {filterChips.map((chip) => (
                            <button
                                key={chip.key}
                                type="button"
                                onClick={() => onFilterChipClick && onFilterChipClick(chip.key)}
                                data-testid={`${testidPrefix}-chip-${chip.key}`}
                                aria-pressed={!!chip.active}
                                className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-medium border transition-colors ${
                                    chip.active
                                        ? "bg-[#131B2F] text-white border-[#131B2F]"
                                        : "bg-white text-[#131B2F] border-[#E5E7EB] hover:bg-[#F5F5F7]"
                                }`}
                            >
                                {chip.label}
                                {typeof chip.count === "number" && (
                                    <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold ${
                                        chip.active ? "bg-white/20 text-white" : "bg-[#F0F1F5] text-[#4B5565]"
                                    }`}>
                                        {chip.count}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    }, [onSearchChange, searchQuery, searchPlaceholder, filterChips, onFilterChipClick, testidPrefix]);

    const listBody = loading ? (
        <div className="p-8 text-center text-xs text-[#4B5565]">
            <Loader2 size={14} className="inline animate-spin" /> Loading…
        </div>
    ) : items.length === 0 ? (
        emptyListState || (
            <div className="p-8 text-center text-xs text-[#4B5565]" data-testid={`${testidPrefix}-empty-list`}>
                Nothing matches the current filters.
            </div>
        )
    ) : (
        <ul className="divide-y divide-[#F0F1F5]" data-testid={`${testidPrefix}-list`}>
            {items.map((it) => {
                const id = getItemId(it);
                const active = id === selectedId;
                return (
                    <li key={id} data-testid={`${testidPrefix}-row-${id}`}>
                        {renderItem(it, {
                            active,
                            onSelect: () => onSelect(it),
                        })}
                    </li>
                );
            })}
        </ul>
    );

    const detailBody = selectedItem ? (
        renderDetail(selectedItem)
    ) : (
        emptyDetailState || (
            <div className="h-full flex items-center justify-center text-center text-sm text-[#4B5565] p-10" data-testid={`${testidPrefix}-empty-detail`}>
                <div className="max-w-xs">
                    <div className="text-base text-[#131B2F] font-medium mb-2">Select a record</div>
                    <div className="text-xs">Pick an item from the list to see the full detail here.</div>
                </div>
            </div>
        )
    );

    return (
        <OperationsShell>
            {headerBanner}
            <MasterDetailLayout
                title={title}
                eyebrow={eyebrow}
                icon={icon}
                primaryAction={primaryAction}
                backHref={backHref}
                backLabel={backLabel}
                toolbar={toolbar}
                listSummary={listSummary}
                list={listBody}
                detail={detailBody}
                hasSelection={!!selectedItem}
                onClearSelection={() => onSelect(null)}
                detailHeader={selectedItem && renderDetailHeader ? renderDetailHeader(selectedItem) : null}
                testid={testidPrefix}
            />
        </OperationsShell>
    );
}
